import { PartyPopper } from "lucide-react";
import { GenerateButton } from "@/components/flowly/GenerationBar/GenerateButton";
import { useLanguage } from "@/contexts/LanguageContext";

interface FlowReadyCtaProps {
  /** Same handler the footer GenerationBar uses — one source of truth. */
  onGenerate: () => void;
  generating: boolean;
  /** Disable when a transient state (e.g. mid-upload) blocks generating while
   *  the card stays mounted. */
  disabled?: boolean;
  creditCost: number;
  costsLoading?: boolean;
  buttonLabel: string;
}

/**
 * "All set!" card shown inline after a flow's LAST step — users don't always
 * notice the fixed GenerationBar at the bottom, so once every step is filled
 * this puts the exact same generate button right where their eyes already are.
 * Mount it only when the flow is complete (or generating). It does NOT
 * self-scroll: the card can mount/unmount as `canGenerate` toggles (e.g. the
 * dialogue word count crossing the min/max bound while typing), and scrolling
 * on each remount would yank the viewport away mid-edit.
 */
export function FlowReadyCta({
  onGenerate,
  generating,
  disabled,
  creditCost,
  costsLoading,
  buttonLabel,
}: FlowReadyCtaProps) {
  const { t } = useLanguage();

  return (
    <div
      className="scroll-mt-24 rounded-2xl border border-brand/30 bg-brand/[0.04] p-4 md:p-5 animate-in fade-in slide-in-from-bottom-2 duration-300"
    >
      <div className="flex flex-col items-center text-center gap-3 md:gap-4">
        <div>
          <h3 className="inline-flex items-center gap-2 text-base md:text-lg font-semibold text-foreground">
            <PartyPopper className="w-5 h-5 text-brand" />
            {t.generate.allReadyTitle}
          </h3>
          <p className="text-xs md:text-sm text-muted-foreground mt-0.5">
            {t.generate.allReadyHint}
          </p>
        </div>
        <GenerateButton
          onGenerate={onGenerate}
          generating={generating}
          disabled={disabled}
          creditCost={creditCost}
          costsLoading={costsLoading}
          buttonLabel={buttonLabel}
        />
      </div>
    </div>
  );
}
